import { useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import type { CreateEquipmentRequest, Equipment, UpdateEquipmentRequest } from "@/api/types";
import { DialogAlert, EquipmentDialogShell, invalidFields } from "./EquipmentDialogShell";

const FIELDS = ["name", "makeModel", "serialNumber", "notes"] as const;
type Field = (typeof FIELDS)[number];

const NAME_TAKEN = "That name is already in use. Pick a different name, or archive the existing machine first.";

/**
 * ef-061-8 — "Add equipment" (`equipment` absent) or "Edit {name}": Name, Make and model, Serial number,
 * Notes. An empty name is refused inline; the server's 409 (an active machine already has the name) is
 * shown inline with `aria-invalid` on Name; any other 400 marks the field it names. An optional value the
 * person blanks out on edit is sent as a single space — the API's "whitespace-only clears it" — so
 * blanking really clears; a value left empty from the start is simply not sent.
 */
export function EquipmentDialog({
  equipment,
  onClose,
  onSaved,
}: {
  /** `null` = add. */
  equipment: Equipment | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(equipment?.name ?? "");
  const [makeModel, setMakeModel] = useState(equipment?.makeModel ?? "");
  const [serialNumber, setSerialNumber] = useState(equipment?.serialNumber ?? "");
  const [notes, setNotes] = useState(equipment?.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<ReadonlySet<string>>(new Set());
  const ids = { name: useId(), makeModel: useId(), serialNumber: useId(), notes: useId() };
  const inFlight = useRef(false);

  const flag = (f: Field): true | undefined => (invalid.has(f) ? true : undefined);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return; // a double-click must not send a second request
    setError(null);
    setInvalid(new Set());

    const trimmedName = name.trim();
    if (trimmedName === "") {
      setInvalid(new Set(["name"]));
      setError("Name is required.");
      return;
    }
    const values = { makeModel: makeModel.trim(), serialNumber: serialNumber.trim(), notes: notes.trim() };

    inFlight.current = true;
    setSaving(true);
    try {
      if (equipment) {
        const body: UpdateEquipmentRequest = { name: trimmedName };
        for (const f of ["makeModel", "serialNumber", "notes"] as const) {
          if (values[f] !== "") body[f] = values[f];
          else if ((equipment[f] ?? "") !== "") body[f] = " "; // whitespace-only clears it
        }
        await demoApi.updateEquipment(equipment.id, body);
      } else {
        const body: CreateEquipmentRequest = { name: trimmedName };
        for (const f of ["makeModel", "serialNumber", "notes"] as const) {
          if (values[f] !== "") body[f] = values[f];
        }
        await demoApi.createEquipment(body);
      }
      onSaved();
      onClose();
    } catch (err) {
      const message = problemMessage(err, "Could not save this equipment.");
      if (err instanceof ApiError && err.status === 409 && /name/i.test(message)) {
        setInvalid(new Set(["name"]));
        setError(NAME_TAKEN);
      } else {
        const hit = invalidFields(problemFieldErrors(err), FIELDS);
        setInvalid(hit);
        setError(message);
      }
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }

  const formId = useId();

  return (
    <EquipmentDialogShell
      title={equipment ? `Edit ${equipment.name}` : "Add equipment"}
      saving={saving}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      {({ alertId }) => (
        <form id={formId} className="emp-dialog__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
          <div className="emp-dialog__field">
            <label htmlFor={ids.name}>Name</label>
            <input
              id={ids.name}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
              aria-invalid={flag("name")}
              aria-describedby={invalid.has("name") ? alertId : undefined}
            />
          </div>
          <div className="emp-dialog__field">
            <label htmlFor={ids.makeModel}>Make and model</label>
            <input
              id={ids.makeModel}
              type="text"
              value={makeModel}
              onChange={(e) => setMakeModel(e.target.value)}
              autoComplete="off"
              aria-invalid={flag("makeModel")}
              aria-describedby={invalid.has("makeModel") ? alertId : undefined}
            />
          </div>
          <div className="emp-dialog__field">
            <label htmlFor={ids.serialNumber}>Serial number</label>
            <input
              id={ids.serialNumber}
              type="text"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              autoComplete="off"
              aria-invalid={flag("serialNumber")}
              aria-describedby={invalid.has("serialNumber") ? alertId : undefined}
            />
          </div>
          <div className="emp-dialog__field">
            <label htmlFor={ids.notes}>Notes</label>
            <textarea
              id={ids.notes}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              aria-invalid={flag("notes")}
              aria-describedby={invalid.has("notes") ? alertId : undefined}
            />
          </div>

          <DialogAlert id={alertId} message={error} />
        </form>
      )}
    </EquipmentDialogShell>
  );
}
